/**
 * FilterContext
 * 
 * Global state management for activity filters.
 * Persists selections to localStorage for cross-session persistence.
 * 
 * Usage:
 * ```tsx
 * import { useFilter } from '@/contexts/FilterContext';
 * 
 * function MyComponent() {
 *   const { filters, setLocation, setTimeMinutes } = useFilter();
 *   // ...
 * }
 * ```
 */

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

// Filter option types
export type LocationOption = 'AT HOME' | 'OUTDOOR' | 'BEDTIME' | 'ON THE MOVE' | 'AT THE TABLE';
export type TimeOption = 5 | 10 | 15 | 30;
export type AgeRangeOption = '2 - 3' | '3 - 4' | '4 - 5';

// Filter state interface
export interface FilterState {
  location: LocationOption | null;
  timeMinutes: TimeOption | null;
  kidsCount: number;
  ageRanges: AgeRangeOption[];
}

// Constants
export const MIN_KIDS = 1;
export const MAX_KIDS = 16;

// Default filter values
export const DEFAULT_FILTERS: FilterState = {
  location: 'AT HOME',
  timeMinutes: 5,
  kidsCount: 1,
  ageRanges: ['3 - 4'],
};

// LocalStorage key
const STORAGE_KEY = 'sparkapp_filters';

// Context value interface
interface FilterContextValue {
  filters: FilterState;
  isDialogOpen: boolean;
  
  // Setters
  setLocation: (location: LocationOption | null) => void;
  setTimeMinutes: (time: TimeOption | null) => void;
  setKidsCount: (count: number) => void;
  toggleAgeRange: (option: AgeRangeOption) => void;
  
  // Actions
  resetFilters: () => void;
  openDialog: () => void;
  closeDialog: () => void;
}

// Create context
const FilterContext = createContext<FilterContextValue | null>(null);

// Provider props
interface FilterProviderProps {
  children: ReactNode;
}

/**
 * Load filters from localStorage
 */
function loadFiltersFromStorage(): FilterState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle any missing fields
      return {
        ...DEFAULT_FILTERS,
        ...parsed,
        ageRanges: Array.isArray(parsed.ageRanges) ? parsed.ageRanges : DEFAULT_FILTERS.ageRanges,
      };
    }
  } catch (error) {
    console.warn('Failed to load filters from localStorage:', error);
  }
  return DEFAULT_FILTERS;
}

/**
 * Save filters to localStorage
 */
function saveFiltersToStorage(filters: FilterState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.warn('Failed to save filters to localStorage:', error);
  }
}

/**
 * FilterProvider Component
 * 
 * Wrap your app with this provider to enable filter state access.
 */
export function FilterProvider({ children }: FilterProviderProps) {
  const [filters, setFilters] = useState<FilterState>(loadFiltersFromStorage);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Persist to localStorage whenever filters change
  useEffect(() => {
    saveFiltersToStorage(filters);
  }, [filters]);

  // Setters
  const setLocation = useCallback((location: LocationOption | null) => {
    setFilters(prev => ({ ...prev, location }));
  }, []);

  const setTimeMinutes = useCallback((timeMinutes: TimeOption | null) => {
    setFilters(prev => ({ ...prev, timeMinutes }));
  }, []);

  const setKidsCount = useCallback((kidsCount: number) => {
    // Clamp between 1 and 16
    const clamped = Math.max(1, Math.min(MAX_KIDS, kidsCount));
    setFilters(prev => ({ ...prev, kidsCount: clamped }));
  }, []);

  const toggleAgeRange = useCallback((option: AgeRangeOption) => {

    console.log('Toggling age range option:', option);
    
    setFilters(prev => {
      const current = prev.ageRanges;
      const next = current.includes(option)
        ? current.filter(r => r !== option)
        : [...current, option];
      return { ...prev, ageRanges: next };
    });

  }, []);

  // Actions
  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const value: FilterContextValue = {
    filters,
    isDialogOpen,
    setLocation,
    setTimeMinutes,
    setKidsCount,
    toggleAgeRange,
    resetFilters,
    openDialog,
    closeDialog,
  };

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  );
}

/**
 * Hook to access filter context
 * 
 * @throws Error if used outside FilterProvider
 */
export function useFilter(): FilterContextValue {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilter must be used within a FilterProvider');
  }
  return context;
}

export default FilterContext;
